import { type ReactElement } from 'react';
import { Form, Link, redirect, useLoaderData, useNavigation } from 'react-router';
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AuthWall, Button } from '@marketplace/ui';
import {
  type ClientProfile,
  type ProviderSubscription,
  type SavedAddressEntry,
} from '@marketplace/contracts';
import { AddressBook, ClientProfileForm } from '../features/account/AddressBook.js';
import { runAccountAction, type AccountActionData } from '../features/account/actions.js';
import { localeOf } from '../features/auth/actions.js';
import { loadMySubscription } from '../features/billing/billing.js';
import { documentsPath } from '../features/billing/documents/documents.js';
import { PastDueBanner } from '../features/billing/PastDueBanner.js';
import { SubscriptionAccountLine } from '../features/billing/SubscriptionAccountLine.js';
import { queryKeys, routeContext } from '../shared/query.js';
import { loadSession, type SessionUser } from '../shared/session.js';

interface AccountData {
  session: SessionUser;
  locale: string;
  /** `W2-T05` §7 — which door to show: edit the provider profile, or make one. */
  isProvider: boolean;
  /** `W2-T04` — the name providers see, and the address book. */
  profile: ClientProfile;
  addresses: SavedAddressEntry[];
  /** `W5-T07` §4.10 — a provider's subscription, for the past-due banner; `null` is no banner. */
  subscription: ProviderSubscription | null;
}

/**
 * The account page — `W2-T10` §4.
 *
 * The smallest honest version: who you are, the way out, and a named place for the thing that does
 * not exist yet. Operator, 2026-09-14: *"the name of the user and a button to open settings. this
 * will lead after to an 'improve subscription tier' page at minimum."*
 *
 * **Sign-out lives here rather than in the header.** A person signs out rarely and reaches for it
 * deliberately; a name that opens an account area is the affordance every other product has taught
 * them. The control posts to the *layout* route's action, which is where the shell's write already
 * was — this page adds a button, not a second way to end a session.
 */
export async function loader({ params, context }: LoaderFunctionArgs): Promise<AccountData> {
  const session = await loadSession(context);

  /**
   * The one guard in the storefront, and it is not the permissions matrix — `W2-T03` owns that.
   * This is the narrower fact that this page has no content without a user: rendering an empty
   * shell and a "you are not signed in" sentence would be a worse version of the login page that
   * already exists.
   *
   * `loadSession` degrades an unreachable API to `null`, so a failure sends a signed-in person to
   * the login form. That is the safe direction: the alternative is an account page that renders
   * somebody's name from a stale cache while the session behind it is gone.
   */
  if (session === null) throw redirect(`/${localeOf(params)}/login`);

  // A failure reads as "not a provider": the worst case is offering `become-a-pro`, which sends a
  // provider on to their profile anyway.
  const { queryClient, api } = context.get(routeContext);
  const own = await queryClient
    .fetchQuery({
      queryKey: queryKeys.myProviderProfile(),
      queryFn: () => api.getMyProviderProfile(),
    })
    .catch(() => null);

  // `fetchQuery`, not `ensureQueryData`: after a write this page must show what was written
  // (`MEM-2026-09-14-3`). No `.catch` — unlike the door above, a book that failed to load rendered
  // as empty would invite the person to re-add what they already have.
  //
  // `W5-T07`: a provider's subscription, asked only of a provider. Like the door, a failure (`403`,
  // `409`, an API that failed) is "no banner" and never breaks the page.
  const [profile, addresses, subscription] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: queryKeys.myClientProfile(),
      queryFn: () => api.getMyClientProfile(),
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.myAddresses(),
      queryFn: () => api.getMyAddresses(),
    }),
    own === null ? Promise.resolve(null) : loadMySubscription(context),
  ]);

  return {
    session,
    locale: localeOf(params),
    isProvider: own !== null,
    profile,
    addresses,
    subscription,
  };
}

/**
 * `W2-T04` — the profile form and the address book's four writes, told apart by `intent`.
 *
 * Every write invalidates both reads: a removal can move the default, which the profile carries, and
 * the profile's name is not in the book. `refetchType: 'all'` for the reason `account-pro` gives.
 */
export async function action({
  request,
  context,
}: ActionFunctionArgs): Promise<AccountActionData | null> {
  const { queryClient, api } = context.get(routeContext);
  const result = await runAccountAction(await request.formData(), api);
  if (result?.saved === true) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.myClientProfile(), refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: queryKeys.myAddresses(), refetchType: 'all' }),
    ]);
  }
  return result;
}

export function Component(): ReactElement {
  const { t } = useTranslation();
  const { session, locale, isProvider, profile, addresses, subscription } =
    useLoaderData<AccountData>();
  const navigation = useNavigation();

  return (
    <article data-testid="account">
      <h1>
        {t('account.title')} — {session.name}
      </h1>

      {/* `W5-T07` §4.10: a failed renewal is said where the provider looks first. */}
      <PastDueBanner subscription={subscription} locale={locale} />
      {/* `W5-T13` §4.6: not entitled — not listed, cannot quote — and the way to fix it. */}
      <SubscriptionAccountLine subscription={subscription} locale={locale} />

      <section className="mp-section" aria-labelledby="account-identity">
        <h2 id="account-identity">{t('account.email.label')}</h2>
        <p className="mp-lead">{session.email}</p>
      </section>

      <section className="mp-section" aria-labelledby="account-profile">
        <h2 id="account-profile">{t('account.profile.title')}</h2>
        <ClientProfileForm profile={profile} />
      </section>

      <section className="mp-section" aria-labelledby="account-addresses">
        <h2 id="account-addresses">{t('account.addresses.title')}</h2>
        <p className="mp-lead">{t('account.addresses.intro')}</p>
        <AddressBook addresses={addresses} />
      </section>

      <section className="mp-section" aria-labelledby="account-pro">
        <h2 id="account-pro">{t('account.pro.title')}</h2>
        <p>
          {isProvider ? (
            <Link to={`/${locale}/account/pro`}>{t('account.pro.edit')}</Link>
          ) : (
            <Link to={`/${locale}/become-a-pro`}>{t('account.pro.become')}</Link>
          )}
        </p>
      </section>

      {/* `W5-T09` §4.9: the provisional receipts and payout statements. */}
      <p>
        <Link to={documentsPath(locale)}>{t('documents.account.link')}</Link>
      </p>

      {/* `W2-T10` §4. Tiers are `BD-16`/`BD-03` and `W5-T07`/`W5-T08` are both `[B]`: there is no
          tier in the schema, no price, and no decision about what a paid one buys. So this is the
          wall `W12-T12` argues for — a sentence, not a disabled button and not a link to nothing —
          and it is where the upgrade goes the day there is one. */}
      <AuthWall title={t('account.plan.title')} description={t('account.plan.body')} />

      {/* Posts to the layout route, which owns the session write — and lands on it, so signing
          out leaves you on the home page rather than on a page whose loader would immediately
          bounce you to the login form. */}
      <Form method="post" action=".." relative="path">
        <input type="hidden" name="intent" value="signout" />
        <Button
          type="submit"
          isPending={navigation.state === 'submitting'}
          pendingLabel={t('auth.pending')}
        >
          {t('account.signOut')}
        </Button>
      </Form>
    </article>
  );
}
